'use client';

import { useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { store } from '@/store';
import { showModal } from '@/store/slices/modalSlice';

import { REVIEW_POPUP_FREE_DELAY_MS } from '../constants';
import { getReviewFlowConfig } from '../remote-config/reviewFlowConfig';
import {
  loadReviewConfig,
  loadReviewState,
} from '../services/reviewFlowService';
import {
  hydrateReviewState,
  resetReviewState,
  setReviewConfig,
  setReviewFlowConfig,
} from '../store/reviewSlice';
import {
  ensureReviewFlowVisualReady,
  warmReviewFlowVisuals,
} from '../utils/reviewFlowAssets';

export default function ReviewFlowProvider() {
  const dispatch = useAppDispatch();

  const uid = useAppSelector((s) => s.auth.user?.uid);
  const reviewFlow = useAppSelector((s) => s.review.reviewFlow);
  const stateLoaded = useAppSelector((s) => s.review.stateLoaded);
  const pendingReview = useAppSelector((s) => s.review.pendingReview);
  const pendingFeatureName = useAppSelector((s) => s.review.pendingFeatureName);
  const activeModal = useAppSelector((s) => s.modal.activeModal);
  const activeSide = useAppSelector((s) => s.side.activeSide);
  const isReviewBlocked = useAppSelector((s) => s.review.blockers.length > 0);

  const configLoadedRef = useRef(false);
  const reviewFlowConfigLoadedRef = useRef(false);
  const visualsWarmedRef = useRef(false);

  useEffect(() => {
    if (configLoadedRef.current) return;
    configLoadedRef.current = true;
    loadReviewConfig().then((cfg) => dispatch(setReviewConfig(cfg)));
  }, [dispatch]);

  useEffect(() => {
    if (reviewFlowConfigLoadedRef.current) return;
    reviewFlowConfigLoadedRef.current = true;
    getReviewFlowConfig().then((cfg) => dispatch(setReviewFlowConfig(cfg)));
  }, [dispatch]);

  useEffect(() => {
    if (!uid) {
      dispatch(resetReviewState());
      return;
    }
    loadReviewState(uid).then((state) => dispatch(hydrateReviewState(state)));
  }, [uid, dispatch]);

  useEffect(() => {
    if (visualsWarmedRef.current || reviewFlow !== 1) return;

    visualsWarmedRef.current = true;

    void ensureReviewFlowVisualReady('entry').finally(() => {
      warmReviewFlowVisuals([
        'trustpilot',
        'feedback',
        'feedbackNotGood',
        'thankyou',
      ]);
    });
  }, [reviewFlow]);

  useEffect(() => {
    if (reviewFlow !== 1 || !stateLoaded || !pendingReview) return;
    if (activeModal || activeSide || isReviewBlocked) return;

    let cancelled = false;

    const timer = window.setTimeout(() => {
      void ensureReviewFlowVisualReady('entry').then(() => {
        if (cancelled) return;

        const s = store.getState();
        if (s.modal.activeModal || s.side.activeSide) return;
        if (s.review.blockers.length > 0) return;
        if (!s.review.pendingReview) return;

        dispatch(
          showModal({
            modalKey: 'ReviewFlow',
            modalData: { featureName: s.review.pendingFeatureName },
          })
        );
      });
    }, REVIEW_POPUP_FREE_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    reviewFlow,
    stateLoaded,
    pendingReview,
    pendingFeatureName,
    activeModal,
    activeSide,
    isReviewBlocked,
    dispatch,
  ]);

  return null;
}
